import { buildStreamUrl } from './buildStreamUrl';

/**
 * clipDownloadUrl — the URL the Download action fetches for ONE clip.
 *
 * This is deliberately independent of which PLAYER renders the cell. SlotCell used to derive the
 * download URL from its `isWebCodecs` flag and hand back `clip.sMovie` verbatim on that branch. That
 * was only ever correct for dragon, whose `sMovie` IS an absolute engine_backend URL. When the
 * WebCodecs gate widened from dragon-only to every local/eview clip (playbackCapability.js), local
 * clips started taking that branch too — and there `sMovie` is a bare VideoStore PATH
 * (`/videostore/vs1/<store>/cam2/2026/08/17/143645.mp4`). The browser fetched that path straight off
 * the viewer's Apache docroot, which has no such file, so every Download produced
 * "Server returned 404" (verified on cs2427: Apache access log, 2026-08-17). Downloads must never
 * ride the player flag.
 *
 * Rules, in order:
 *   - no clip / not ready              -> '' (caller shows "No clip to download")
 *   - `sMovie` already absolute http(s) -> use it as-is (dragon directMedia)
 *   - anything else                     -> buildStreamUrl, which attaches auth (session for the
 *                                          serving NVR, user:pass for an eview member, token for
 *                                          cloud) and `&overlay=1&mp4=1` so the SAVED file carries
 *                                          the server OSD burn.
 *
 * @param {object|null} clip        the active clip ({ sMovie, fCloudVS, … })
 * @param {object}      streamCtx   the cell's stream context (sSess, username/password, fEview, profile, …)
 * @param {object}      [opts]
 * @param {boolean}     [opts.clipReady=true]  false while a cloud-legacy clip is still being cached
 * @returns {string}
 */
export const clipDownloadUrl = (clip, streamCtx = {}, { clipReady = true } = {}) => {
  if (!clip || !clipReady) return '';
  const file = clip.sMovie;
  if (!file) return '';
  // Dragon directMedia: an absolute engine_backend MP4, fetched as-is (no pbserver in front of it,
  // so no session and no postoverlay burn are possible or needed).
  if (/^https?:\/\//i.test(file)) return file;
  return buildStreamUrl({ file, cloud: clip.fCloudVS, ...streamCtx, overlay: true });
};

export default clipDownloadUrl;
